import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";

const AUTOPLAY_MS = 5000;
const MAX_SLIDES = 5;

const btnRadius = (style) => (style === "pill" ? "9999px" : style === "sharp" ? "2px" : "12px");

const ALIGN = {
  center: "items-center text-center left-0 right-0 mx-auto",
  right: "items-end text-right right-0",
  left: "items-start text-left left-0",
};

function Slide({ b, radius, eager }) {
  const overlay = Math.min(Math.max(Number(b.overlay_strength ?? 60), 0), 100) / 100;
  const align = ALIGN[b.text_alignment] || ALIGN.left;
  const cta = b.button_text || "Belanja Sekarang";
  const link = b.button_link || b.link_url || "/katalog";
  return (
    <div className="relative w-full h-full shrink-0 bg-[#1A1A1A]" data-testid="hero-slide">
      {b.image_url && (
        <>
          {b.mobile_image_url ? (
            <>
              <img src={b.mobile_image_url} alt={b.title} loading={eager ? "eager" : "lazy"}
                className="md:hidden absolute inset-0 w-full h-full object-cover" />
              <img src={b.image_url} alt={b.title} loading={eager ? "eager" : "lazy"}
                className="hidden md:block absolute inset-0 w-full h-full object-cover" />
            </>
          ) : (
            <img src={b.image_url} alt={b.title} loading={eager ? "eager" : "lazy"}
              className="absolute inset-0 w-full h-full object-cover" />
          )}
        </>
      )}
      <div className="absolute inset-0"
        style={{ background: `linear-gradient(to top, rgba(0,0,0,${(overlay * 0.9).toFixed(2)}) 0%, rgba(0,0,0,${(overlay * 0.4).toFixed(2)}) 45%, transparent 100%)` }} />
      <div className={`absolute bottom-0 p-6 sm:p-10 text-white max-w-xl flex flex-col ${align}`}>
        {b.badge && (
          <div className="inline-block w-fit text-[10px] uppercase tracking-widest brand-bg-primary px-3 py-1 rounded-full font-bold mb-3" data-testid="hero-slide-badge">
            {b.badge}
          </div>
        )}
        <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight leading-[1.05] mb-3">{b.title}</h2>
        {b.subtitle && <p className="text-sm sm:text-base text-white/85 mb-5">{b.subtitle}</p>}
        <Link to={link}
          className="inline-flex w-fit items-center gap-2 bg-white text-[#1A1A1A] font-bold px-5 py-3 hover:text-white transition-colors hover:[background:var(--brand-primary)]"
          style={{ borderRadius: radius }} data-testid="hero-slide-cta">
          {cta} <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}

function FallbackHero({ branding, radius }) {
  return (
    <section
      className="relative rounded-3xl overflow-hidden h-[280px] md:h-[420px] grid place-items-center p-8"
      style={{ background: "linear-gradient(135deg, color-mix(in srgb, var(--brand-primary) 18%, var(--brand-background)), color-mix(in srgb, var(--brand-accent) 12%, var(--brand-background)))" }}
      data-testid="hero-fallback"
    >
      <div className="text-center max-w-xl">
        <h1 className="font-display text-3xl sm:text-5xl font-black text-[color:var(--brand-text)]">{branding.general.name}</h1>
        <p className="mt-3 text-[color:var(--brand-text)] opacity-70">
          {branding.general.description || branding.general.tagline || "Belanja mudah, langsung dari toko kami"}
        </p>
        <Link to="/katalog"
          className="mt-6 inline-flex items-center gap-2 text-white font-bold px-6 py-3 transition-opacity hover:opacity-90"
          style={{ backgroundColor: "var(--brand-primary)", borderRadius: radius }}
          data-testid="hero-fallback-cta">
          Belanja Sekarang <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
}

export const HeroBannerSlider = ({ banners }) => {
  const { branding } = useBranding();
  const radius = btnRadius(branding.theme.button_style);
  const slides = (banners || [])
    .filter((b) => b.is_active !== false && b.enabled !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .slice(0, MAX_SLIDES);
  const count = slides.length;

  const [idx, setIdx] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [hidden, setHidden] = useState(false);
  const touchX = useRef(null);

  const go = useCallback((n) => {
    if (count < 1) return;
    setIdx(((n % count) + count) % count);
  }, [count]);

  useEffect(() => {
    if (count < 2 || hovered || hidden) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [count, hovered, hidden]);

  useEffect(() => {
    const onVis = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  if (count === 0) return <FallbackHero branding={branding} radius={radius} />;

  return (
    <section
      className="relative rounded-3xl overflow-hidden h-[280px] md:h-[420px] group outline-none"
      data-testid="hero-banner-slider"
      data-slide-count={count}
      data-active-slide={idx}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "ArrowLeft") go(idx - 1); if (e.key === "ArrowRight") go(idx + 1); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) (dx > 0 ? go(idx - 1) : go(idx + 1));
        touchX.current = null;
      }}
    >
      <div className="flex h-full transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${idx * 100}%)` }}>
        {slides.map((b, i) => <Slide key={b.id || i} b={b} radius={radius} eager={i === 0} />)}
      </div>

      {count > 1 && (
        <>
          <button aria-label="Sebelumnya" onClick={() => go(idx - 1)}
            className="hidden md:grid place-items-center absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/40 transition-colors opacity-0 group-hover:opacity-100"
            data-testid="hero-prev-btn">
            <ChevronLeft size={20} />
          </button>
          <button aria-label="Berikutnya" onClick={() => go(idx + 1)}
            className="hidden md:grid place-items-center absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/40 transition-colors opacity-0 group-hover:opacity-100"
            data-testid="hero-next-btn">
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2" data-testid="hero-dots">
            {slides.map((_, i) => (
              <button key={i} aria-label={`Slide ${i + 1}`} onClick={() => go(i)}
                className="h-2 rounded-full transition-all duration-300"
                style={{ width: i === idx ? 22 : 8, backgroundColor: i === idx ? "var(--brand-accent)" : "rgba(255,255,255,0.5)" }}
                data-testid={`hero-dot-${i}`} />
            ))}
          </div>
        </>
      )}
    </section>
  );
};
